/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 탭바 컨트롤러 클래스를 정의한다.
 *
 * @file /src/components/tab/elements/Tab.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 19.
 */
import { Panel } from '@components/panel/Panel';
import { Menu } from '@components/menu/Menu';
import { Tab as TabPanel } from '@components/tab/Tab';
import { Control } from '@elements/control/Control';
import { Dom } from '@utils/Dom';
import { Scroller } from '@utils/Scroller';
import { Language } from '@utils/Language';

export class Tab extends Control {
    /**
     * 엘리먼트 타입
     */
    type: string = 'control';

    /**
     * 연결된 패널
     */
    panel: Panel;

    /**
     * 활성화 여부
     */
    pressed: boolean = false;

    /**
     * 메뉴
     */
    menu: Menu = null;

    /**
     * 탭바 엘리먼트를 정의한다.
     *
     * @param properties - 엘리먼트 초기설정
     * @param bar - 부모가 되는 탭바
     */
    constructor(panel: Panel) {
        super();

        this.panel = panel;
        this.icon = this.panel.getTitle()?.icon ?? 'mi mi-bars';
        this.text = this.panel.getTitle()?.text ?? 'UNTITLED';
        this.movable = this.panel.movable === true;
        this.closable = this.panel.closable === true;

        this.$getElement().setData('tab', this);
    }

    /**
     * 탭 패널 컴포넌트를 가져온다.
     *
     * @returns 탭 패널
     */
    getComponent(): TabPanel {
        return this.component as TabPanel;
    }

    /**
     * 탭의 패널을 가져온다.
     *
     * @returns 패널
     */
    getPanel(): Panel {
        return this.panel;
    }

    /**
     * 탭 Id를 가져온다.
     */
    getTabId(): string {
        return this.panel.getId();
    }

    /**
     * 컨테이너 DOM을 가져온다 (드래그 앤 드롭용)
     */
    getContainer(): Dom {
        return this.getComponent().getTabbar().$getTabs();
    }

    /**
     * 스크롤러를 가져온다 (드래그 앤 드롭용)
     */
    getScroller(): Scroller {
        return this.getComponent().getTabbar().getScroller();
    }

    /**
     * 작업 메뉴를 가져온다.
     *
     * @returns 작업 메뉴
     */
    getMenu(): Menu {
        if (this.closable === true && this.menu === null) {
            this.menu = new Menu({
                $target: this.$getButton(),
                menus: [
                    {
                        type: 'button',
                        text: Language.get('components.tab.close'),
                        icon: 'mi mi-close',
                        handler: async () => {
                            this.getPanel().remove();
                            // this.remove();
                        },
                    },
                ],
                listeners: {
                    beforeShow: () => {
                        this.$getButton().addClass('expand');
                    },
                    show: (menu) => {
                        //menu.$getComponent().setStyle('width', this.$element.getOuterWidth() + 'px');
                    },
                    hide: () => {
                        this.$getButton().removeClass('expand');
                    },
                },
            });

            //this.menu.setParent(this.panel);
        }

        return this.menu;
    }

    /**
     * 버튼 DOM 을 가져온다.
     *
     * @returns 버튼 DOM
     */
    $getButton(): Dom {
        if (this.$button === null) {
            this.$button = super.$getButton();

            this.$button.on('contextmenu', async (_, e) => {
                if (this.getComponent().hasEvent('openMenu') == false && this.closable == false) {
                    return;
                }

                const menu = new Menu({
                    $target: this.$getButton(),
                    removeOnHide: true,
                    listeners: {
                        beforeShow: () => {
                            this.$getButton().addClass('expand');
                        },
                        hide: () => {
                            this.$getButton().removeClass('expand');
                        },
                    },
                });

                await this.getComponent().fireEvent('openMenu', [menu, this.getPanel(), this.getComponent()]);

                if (this.closable == true && menu.hasMenuByName('close') === false) {
                    menu.addMenu({
                        icon: 'mi mi-close',
                        name: 'close',
                        text: Language.get('components.tab.close'),
                        handler: async () => {
                            this.getPanel().remove();
                        },
                    });
                }

                menu.show();
                e.preventDefault();
            });
        }

        return this.$button;
    }

    /**
     * 컨트롤을 활성화한다.
     */
    async active(): Promise<void> {
        this.scrollTo();
        await this.getComponent().active(this.panel.getId());
    }

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.$icon = null;
        this.$text = null;

        await super.remove(removeDom);
    }

    /**
     * 탭이 변경되었을 때 이벤트를 처리한다.
     */
    async onUpdate(): Promise<void> {
        this.getComponent().getTabbar().storeTabs();
    }
}
